import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { formatDateBR, formatDateOnlyBR } from '@/utils/dateUtils';
import api from '@/lib/api';
import { Receipt, ArrowLeft, Upload, History, PlusCircle, Phone, MessageCircle, Mail, CheckCircle, XCircle, Ban, Loader2, Trash2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

type ViewMode = 'list' | 'import' | 'detail';

interface CobrancaLoteResumo {
  id: number;
  period_start: string;
  period_end: string;
  status_filter: string;
  total_value: number;
  total_clients: number;
  total_contacts_phone: number;
  total_contacts_email: number;
  total_contacts_whatsapp: number;
  total_paid_count: number;
  total_paid_value: number;
  total_open_count: number;
  total_open_value: number;
  created_at: string;
}

interface CobrancaItemRow {
  id: number;
  cobranca_lote_id: number;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  description: string | null;
  due_date: string;
  value: number;
  last_contact_at: string | null;
  last_contact_type: string | null;
  promessa_pagamento: boolean;
  promessa_data: string | null;
  pago?: boolean;
  data_pagamento?: string | null;
}

interface CobrancaContatoRow {
  id: number;
  contact_type: string;
  contact_status: string;
  observation: string | null;
  promessa_pagamento: boolean;
  promessa_data: string | null;
  created_at: string;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

const CONTACT_TYPE_LABEL: Record<string, string> = {
  TELEFONE: 'Telefone',
  WHATSAPP: 'WhatsApp',
  EMAIL: 'E-mail',
};
const CONTACT_STATUS_LABEL: Record<string, string> = {
  ATENDIDO: 'Atendido',
  NAO_ATENDIDO: 'Não atendido',
  REJEITADO: 'Rejeitado',
};

const IMPORT_STEP_MESSAGES = [
  'Buscando pagamentos no Asaas...',
  'Buscando dados dos clientes (pode levar 1–2 min)...',
  'Salvando lote e itens...',
];

const CentralCobranca = () => {
  const [view, setView] = useState<ViewMode>('list');
  const [lotes, setLotes] = useState<CobrancaLoteResumo[]>([]);
  const [loadingLotes, setLoadingLotes] = useState(true);
  const [selectedLoteId, setSelectedLoteId] = useState<number | null>(null);
  const [loteDetail, setLoteDetail] = useState<{ lote: CobrancaLoteResumo; itens: CobrancaItemRow[] } | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Import form
  const [importDueDateGe, setImportDueDateGe] = useState('');
  const [importDueDateLe, setImportDueDateLe] = useState('');
  const [importStatus, setImportStatus] = useState('OVERDUE');
  const [importing, setImporting] = useState(false);
  const [importStepMessage, setImportStepMessage] = useState('');

  // Registrar contato modal
  const [contatoModalItemId, setContatoModalItemId] = useState<number | null>(null);
  const [contatoType, setContatoType] = useState('TELEFONE');
  const [contatoStatus, setContatoStatus] = useState('ATENDIDO');
  const [contatoObservation, setContatoObservation] = useState('');
  const [contatoPromessa, setContatoPromessa] = useState(false);
  const [contatoPromessaData, setContatoPromessaData] = useState('');
  const [savingContato, setSavingContato] = useState(false);

  // Histórico modal
  const [historicoItemId, setHistoricoItemId] = useState<number | null>(null);
  const [historicoList, setHistoricoList] = useState<CobrancaContatoRow[]>([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);

  // Apagar lote modal (pode ser aberto da lista ou do detalhe)
  const [loteToDeleteId, setLoteToDeleteId] = useState<number | null>(null);
  const [showDeleteLoteModal, setShowDeleteLoteModal] = useState(false);
  const [deleteLotePassword, setDeleteLotePassword] = useState('');
  const [deletingLote, setDeletingLote] = useState(false);

  // Marcar como pago modal
  const [marcarPagoItemId, setMarcarPagoItemId] = useState<number | null>(null);
  const [marcarPagoData, setMarcarPagoData] = useState('');
  const [marcarPagoSaving, setMarcarPagoSaving] = useState(false);

  const fetchLotes = async () => {
    setLoadingLotes(true);
    try {
      const data = await api.cobrancaListLotes();
      setLotes(Array.isArray(data) ? data : []);
    } catch (e: any) {
      toast({ title: 'Erro ao carregar lotes', description: e?.message || 'Tente novamente.', variant: 'destructive' });
    } finally {
      setLoadingLotes(false);
    }
  };

  useEffect(() => {
    if (view === 'list') fetchLotes();
  }, [view]);

  useEffect(() => {
    if (!importing) {
      setImportStepMessage('');
      return;
    }
    setImportStepMessage(IMPORT_STEP_MESSAGES[0]);
    let idx = 0;
    const interval = setInterval(() => {
      idx = (idx + 1) % IMPORT_STEP_MESSAGES.length;
      setImportStepMessage(IMPORT_STEP_MESSAGES[idx]);
    }, 4000);
    return () => clearInterval(interval);
  }, [importing]);

  useEffect(() => {
    if (selectedLoteId != null && view === 'detail') {
      setLoadingDetail(true);
      api
        .cobrancaGetLote(selectedLoteId)
        .then((res) => {
          setLoteDetail({ lote: res.lote, itens: res.itens || [] });
        })
        .catch((e: any) => {
          toast({ title: 'Erro ao carregar lote', description: e?.message, variant: 'destructive' });
        })
        .finally(() => setLoadingDetail(false));
    } else {
      setLoteDetail(null);
    }
  }, [selectedLoteId, view]);

  const handleImport = async () => {
    if (!importDueDateGe || !importDueDateLe) {
      toast({ title: 'Preencha as datas do período', variant: 'destructive' });
      return;
    }
    setImporting(true);
    try {
      const res = await api.cobrancaImport({
        dueDateGe: importDueDateGe,
        dueDateLe: importDueDateLe,
        status: importStatus,
      });
      toast({ title: res.message || 'Lote importado.' });
      setView('list');
      if (res.lote?.id) {
        setSelectedLoteId(res.lote.id);
        setView('detail');
        setLoteDetail({ lote: res.lote, itens: res.itens || [] });
      }
      fetchLotes();
    } catch (e: any) {
      toast({
        title: 'Erro ao importar',
        description: e?.message || e?.response?.data?.error || 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
    }
  };

  const openRegistrarContato = (itemId: number) => {
    setContatoModalItemId(itemId);
    setContatoType('TELEFONE');
    setContatoStatus('ATENDIDO');
    setContatoObservation('');
    setContatoPromessa(false);
    setContatoPromessaData('');
  };

  const saveContato = async () => {
    if (contatoModalItemId == null) return;
    setSavingContato(true);
    try {
      await api.cobrancaCreateContato(contatoModalItemId, {
        contact_type: contatoType,
        contact_status: contatoStatus,
        observation: contatoObservation.trim() || undefined,
        promessa_pagamento: contatoPromessa,
        promessa_data: contatoPromessa && contatoPromessaData ? contatoPromessaData : undefined,
      });
      toast({ title: 'Contato registrado.' });
      setContatoModalItemId(null);
      if (selectedLoteId != null) {
        const res = await api.cobrancaGetLote(selectedLoteId);
        setLoteDetail({ lote: res.lote, itens: res.itens || [] });
      }
    } catch (e: any) {
      toast({ title: 'Erro ao salvar contato', description: e?.message, variant: 'destructive' });
    } finally {
      setSavingContato(false);
    }
  };

  const handleDeleteLote = async () => {
    const idToDelete = loteToDeleteId ?? selectedLoteId;
    if (idToDelete == null || !deleteLotePassword.trim()) {
      toast({ title: 'Informe sua senha para confirmar a exclusão.', variant: 'destructive' });
      return;
    }
    setDeletingLote(true);
    try {
      await api.cobrancaDeleteLote(idToDelete, deleteLotePassword.trim());
      toast({ title: 'Lote excluído com sucesso.' });
      setShowDeleteLoteModal(false);
      setDeleteLotePassword('');
      setLoteToDeleteId(null);
      setView('list');
      setSelectedLoteId(null);
      setLoteDetail(null);
      fetchLotes();
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Erro ao excluir lote.';
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setDeletingLote(false);
    }
  };

  const handleMarcarPago = async () => {
    if (marcarPagoItemId == null || !marcarPagoData.trim()) {
      toast({ title: 'Informe a data de pagamento.', variant: 'destructive' });
      return;
    }
    setMarcarPagoSaving(true);
    try {
      await api.cobrancaMarcarItemPago(marcarPagoItemId, marcarPagoData.trim());
      toast({ title: 'Item marcado como pago.' });
      setMarcarPagoItemId(null);
      setMarcarPagoData('');
      if (selectedLoteId != null) {
        const res = await api.cobrancaGetLote(selectedLoteId);
        setLoteDetail({ lote: res.lote, itens: res.itens || [] });
      }
    } catch (e: any) {
      toast({
        title: e?.response?.data?.error || e?.message || 'Erro ao marcar como pago',
        variant: 'destructive',
      });
    } finally {
      setMarcarPagoSaving(false);
    }
  };

  const openHistorico = async (itemId: number) => {
    setHistoricoItemId(itemId);
    setLoadingHistorico(true);
    try {
      const data = await api.cobrancaGetContatos(itemId);
      setHistoricoList(Array.isArray(data) ? data : []);
    } catch (e: any) {
      toast({ title: 'Erro ao carregar histórico', description: e?.message, variant: 'destructive' });
      setHistoricoList([]);
    } finally {
      setLoadingHistorico(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 p-4 md:p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Receipt className="h-7 w-7" />
              Central de Cobrança
            </h1>
            <p className="text-muted-foreground">
              Importe boletos por período do Asaas e registre o histórico de contatos.
            </p>
          </div>
        </div>

        {view === 'list' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>Lotes importados</CardTitle>
                <CardDescription>Clique em um lote para ver os itens e registrar contatos.</CardDescription>
              </div>
              <Button onClick={() => setView('import')}>
                <Upload className="h-4 w-4 mr-2" />
                Importar novo lote
              </Button>
            </CardHeader>
            <CardContent>
              {loadingLotes ? (
                <p className="text-muted-foreground">Carregando...</p>
              ) : lotes.length === 0 ? (
                <p className="text-muted-foreground">Nenhum lote ainda. Importe um lote para começar.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Período</TableHead>
                      <TableHead>Clientes</TableHead>
                      <TableHead>Valor total</TableHead>
                      <TableHead>Contatos (Tel / E-mail / WA)</TableHead>
                      <TableHead>Criado em</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lotes.map((l) => (
                      <TableRow
                        key={l.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => {
                          setSelectedLoteId(l.id);
                          setView('detail');
                        }}
                      >
                        <TableCell>
                          {formatDateOnlyBR(l.period_start)} – {formatDateOnlyBR(l.period_end)}
                        </TableCell>
                        <TableCell>{l.total_clients}</TableCell>
                        <TableCell>{formatCurrency(l.total_value)}</TableCell>
                        <TableCell>
                          {l.total_contacts_phone} / {l.total_contacts_email} / {l.total_contacts_whatsapp}
                        </TableCell>
                        <TableCell>{formatDateBR(l.created_at)}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setSelectedLoteId(l.id);
                              setView('detail');
                            }}
                          >
                            Abrir
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Apagar lote"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setLoteToDeleteId(l.id);
                              setShowDeleteLoteModal(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {view === 'import' && (
          <Card>
            <CardHeader>
              <CardTitle>Importar lote</CardTitle>
              <CardDescription>
                Informe o período de vencimento e o status (OVERDUE = vencidos, PENDING = pendentes/lembrete).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 max-w-md">
              {importing && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 flex items-start gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">{importStepMessage}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      A primeira importação pode levar 1 a 2 minutos. Não feche a página.
                    </p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data início (vencimento)</Label>
                  <Input
                    type="date"
                    value={importDueDateGe}
                    onChange={(e) => setImportDueDateGe(e.target.value)}
                    disabled={importing}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data fim (vencimento)</Label>
                  <Input
                    type="date"
                    value={importDueDateLe}
                    onChange={(e) => setImportDueDateLe(e.target.value)}
                    disabled={importing}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={importStatus} onValueChange={setImportStatus} disabled={importing}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OVERDUE">OVERDUE (vencidos)</SelectItem>
                    <SelectItem value="PENDING">PENDING (pendentes)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setView('list')} disabled={importing}>
                  Cancelar
                </Button>
                <Button onClick={handleImport} disabled={importing}>
                  {importing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importando...
                    </>
                  ) : (
                    'Importar'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {view === 'detail' && loteDetail && (
          <>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setView('list')}>
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Voltar
                </Button>
                <span className="text-sm text-muted-foreground">
                  Lote: {formatDateOnlyBR(loteDetail.lote.period_start)} – {formatDateOnlyBR(loteDetail.lote.period_end)}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => {
                  setDeleteLotePassword('');
                  setShowDeleteLoteModal(true);
                }}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Apagar lote
              </Button>
            </div>
            <Card>
              <CardHeader>
                <CardTitle>Itens do lote</CardTitle>
                <CardDescription>
                  Valor total: {formatCurrency(loteDetail.lote.total_value)} • {loteDetail.itens.length} itens
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingDetail ? (
                  <p className="text-muted-foreground">Carregando...</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>WhatsApp</TableHead>
                          <TableHead>E-mail</TableHead>
                          <TableHead>Descrição</TableHead>
                          <TableHead>Vencimento</TableHead>
                          <TableHead>Valor</TableHead>
                          <TableHead>Pago</TableHead>
                          <TableHead>Último contato</TableHead>
                          <TableHead>Promessa</TableHead>
                          <TableHead>Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loteDetail.itens.map((item) => {
                          const isPago = Boolean(item.pago);
                          return (
                            <TableRow
                              key={item.id}
                              className={cn(
                                isPago && 'bg-muted/50 text-muted-foreground'
                              )}
                            >
                              <TableCell className={cn('font-medium', isPago && 'text-muted-foreground')}>
                                {item.customer_name || '–'}
                              </TableCell>
                              <TableCell>{item.customer_phone || '–'}</TableCell>
                              <TableCell>{item.customer_email || '–'}</TableCell>
                              <TableCell className="max-w-[180px] truncate">{item.description || '–'}</TableCell>
                              <TableCell>{formatDateOnlyBR(item.due_date)}</TableCell>
                              <TableCell>{formatCurrency(item.value)}</TableCell>
                              <TableCell>
                                {isPago ? (
                                  <span className="text-muted-foreground">
                                    {item.data_pagamento ? formatDateOnlyBR(item.data_pagamento) : 'Pago'}
                                  </span>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-primary border-primary/50"
                                    onClick={() => {
                                      setMarcarPagoItemId(item.id);
                                      setMarcarPagoData('');
                                    }}
                                    title="Marcar como pago"
                                  >
                                    <Check className="h-4 w-4 mr-1" />
                                    Marcar pago
                                  </Button>
                                )}
                              </TableCell>
                              <TableCell>
                                {item.last_contact_at
                                  ? `${formatDateBR(item.last_contact_at)} (${CONTACT_TYPE_LABEL[item.last_contact_type || ''] || item.last_contact_type})`
                                  : '–'}
                              </TableCell>
                              <TableCell>
                                {item.promessa_pagamento
                                  ? `Sim ${item.promessa_data ? formatDateOnlyBR(item.promessa_data) : ''}`
                                  : 'Não'}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  {!isPago && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => openRegistrarContato(item.id)}
                                      title="Registrar novo contato"
                                    >
                                      <PlusCircle className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openHistorico(item.id)}
                                    title="Histórico de contatos"
                                  >
                                    <History className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Modal: Registrar novo contato */}
        <Dialog open={contatoModalItemId != null} onOpenChange={(open) => !open && setContatoModalItemId(null)}>
          <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden border-0 shadow-xl">
            <div className="bg-primary text-primary-foreground px-6 py-4">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-primary-foreground">
                  Registrar novo contato
                </DialogTitle>
                <DialogDescription className="text-primary-foreground/90 text-sm mt-1">
                  A data será registrada automaticamente. Ao escolher E-mail e salvar, um e-mail com o boleto será enviado ao cliente.
                </DialogDescription>
              </DialogHeader>
            </div>
            <div className="px-6 py-5 space-y-5 bg-background">
              <div className="space-y-3">
                <Label className="text-sm font-medium">Tipo de contato</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'TELEFONE', label: 'Telefone', icon: Phone },
                    { value: 'WHATSAPP', label: 'WhatsApp', icon: MessageCircle },
                    { value: 'EMAIL', label: 'E-mail', icon: Mail },
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setContatoType(value)}
                      className={cn(
                        'flex flex-col items-center justify-center gap-2 rounded-xl border-2 py-4 px-3 transition-all',
                        contatoType === value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-muted hover:border-primary/50 hover:bg-muted/50 text-muted-foreground'
                      )}
                    >
                      <Icon className="h-7 w-7" />
                      <span className="text-sm font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <Label className="text-sm font-medium">Status do contato</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'ATENDIDO', label: 'Atendido', icon: CheckCircle },
                    { value: 'NAO_ATENDIDO', label: 'Não atendido', icon: XCircle },
                    { value: 'REJEITADO', label: 'Rejeitado', icon: Ban },
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setContatoStatus(value)}
                      className={cn(
                        'flex flex-col items-center justify-center gap-2 rounded-xl border-2 py-3 px-2 transition-all',
                        contatoStatus === value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-muted hover:border-primary/50 hover:bg-muted/50 text-muted-foreground'
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-xs font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Observação do atendimento</Label>
                <textarea
                  className="flex min-h-[88px] w-full rounded-lg border border-input bg-muted/30 px-3 py-2.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={contatoObservation}
                  onChange={(e) => setContatoObservation(e.target.value)}
                  placeholder="Opcional"
                />
              </div>
              <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="promessa"
                    checked={contatoPromessa}
                    onChange={(e) => setContatoPromessa(e.target.checked)}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
                  />
                  <Label htmlFor="promessa" className="text-sm font-medium cursor-pointer">
                    Promessa de pagamento?
                  </Label>
                </div>
                {contatoPromessa && (
                  <div className="pl-7">
                    <Label className="text-xs text-muted-foreground mb-1.5 block">Data prevista</Label>
                    <Input
                      type="date"
                      value={contatoPromessaData}
                      onChange={(e) => setContatoPromessaData(e.target.value)}
                      className="rounded-lg"
                    />
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="px-6 py-4 border-t bg-muted/30 flex-row gap-2 justify-end">
              <Button variant="outline" onClick={() => setContatoModalItemId(null)}>
                Cancelar
              </Button>
              <Button onClick={saveContato} disabled={savingContato}>
                {savingContato ? 'Salvando...' : 'Salvar contato'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Confirmar exclusão do lote */}
        <Dialog
          open={showDeleteLoteModal}
          onOpenChange={(open) => {
            if (!open) {
              setShowDeleteLoteModal(false);
              setDeleteLotePassword('');
              setLoteToDeleteId(null);
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-destructive">Apagar lote</DialogTitle>
              <DialogDescription>
                Esta ação não pode ser desfeita. O lote e todos os itens e contatos serão excluídos. Digite sua senha
                para confirmar:
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="delete-lote-password" className="sr-only">
                Sua senha
              </Label>
              <Input
                id="delete-lote-password"
                type="password"
                placeholder="Senha"
                value={deleteLotePassword}
                onChange={(e) => setDeleteLotePassword(e.target.value)}
                disabled={deletingLote}
                className="rounded-lg"
                autoComplete="current-password"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteLoteModal(false);
                  setDeleteLotePassword('');
                }}
                disabled={deletingLote}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteLote}
                disabled={deletingLote || !deleteLotePassword.trim()}
              >
                {deletingLote ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  'Excluir lote'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Marcar como pago */}
        <Dialog
          open={marcarPagoItemId != null}
          onOpenChange={(open) => {
            if (!open) {
              setMarcarPagoItemId(null);
              setMarcarPagoData('');
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Marcar como pago</DialogTitle>
              <DialogDescription>
                Informe a data em que o pagamento foi realizado. O item ficará em destaque cinza e não será mais
                sugerido para novos contatos.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="marcar-pago-data">Data de pagamento</Label>
              <Input
                id="marcar-pago-data"
                type="date"
                value={marcarPagoData}
                onChange={(e) => setMarcarPagoData(e.target.value)}
                disabled={marcarPagoSaving}
                className="mt-2 rounded-lg"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setMarcarPagoItemId(null);
                  setMarcarPagoData('');
                }}
                disabled={marcarPagoSaving}
              >
                Cancelar
              </Button>
              <Button onClick={handleMarcarPago} disabled={marcarPagoSaving || !marcarPagoData.trim()}>
                {marcarPagoSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Confirmar'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Histórico de contatos */}
        <Dialog open={historicoItemId != null} onOpenChange={(open) => !open && setHistoricoItemId(null)}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Histórico de contatos</DialogTitle>
              <DialogDescription>Linha do tempo de contatos realizados para este item.</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {loadingHistorico ? (
                <p className="text-muted-foreground">Carregando...</p>
              ) : historicoList.length === 0 ? (
                <p className="text-muted-foreground">Nenhum contato registrado ainda.</p>
              ) : (
                <ul className="space-y-4">
                  {historicoList.map((c) => (
                    <li key={c.id} className="border-l-2 border-primary/30 pl-4 pb-4">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        {CONTACT_TYPE_LABEL[c.contact_type] || c.contact_type} •{' '}
                        {CONTACT_STATUS_LABEL[c.contact_status] || c.contact_status}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{formatDateBR(c.created_at)}</div>
                      {c.observation && (
                        <p className="text-sm mt-1 text-muted-foreground">{c.observation}</p>
                      )}
                      {c.promessa_pagamento && (
                        <p className="text-sm mt-1">
                          Promessa: {c.promessa_data ? formatDateOnlyBR(c.promessa_data) : 'Sim'}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CentralCobranca;
